import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { clone } from "@formant/core/common/clone";
import { ConnectionError } from "@formant/core/common/ConnectionError";
import { defined } from "@formant/core/common/defined";
import { isNullOrEmpty } from "@formant/core/common/isNullOrEmpty";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { toUnixTime } from "@formant/date-fns/toUnixTime";
import { duration } from "@formant/core/model/duration";
import { ICommand } from "@formant/core/model/ICommand";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IDictionary } from "@formant/core/model/IDictionary";
import { IValidation } from "@formant/core/model/IValidation";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";
import { animate } from "@formant/ui/utils/animate";

import { track } from "../../analytics";
import { deviceClient } from "../../clients";
import { lookupCommandInfo } from "../../model/builtInCommand";
import {
    appStore,
    authStore,
    commandStore,
    deviceStore,
    observeStore,
    onlineDevicesStore,
    userStore,
} from "../../stores";
import { formatDateTime } from "../../utils/formatDateTime";
import { Buttons } from "../common/Buttons";
import { DateTimeField } from "../common/DateTimeField";
import { Escape } from "../common/Escape";
import { Form } from "../common/Form";
import { Heading3 } from "../common/Heading3";
import { Popup } from "../common/Popup";
import { StandardButton } from "../common/StandardButton";
import { ValidationMessages } from "../common/ValidationMessages";
import * as styles from "./NewCommandPopup.scss";

export interface ICommandForm {
    parameter: string;
    meta: IDictionary;
}

export interface INewCommandPopupProps {
    device: IDeviceDetails;
    commandTemplate: ICommandTemplate;
}

@observer
export class NewCommandPopup extends Component<INewCommandPopupProps> {
    @observable
    private validation: IValidation | undefined;
    @observable
    private form: ICommandForm = clone({
        parameter: this.props.commandTemplate?.parameterValue || "",
        meta: this.props.commandTemplate?.parameterMeta || {},
    });
    @observable
    private loading: boolean = false;
    @observable
    private visible: boolean = true;

    @observable
    private onDemandStreams: string[] | undefined;

    private time: Date = observeStore.time;

    @observable
    private startTime: Date = new Date(
        observeStore.time.getTime() - 30 * duration.second,
    );

    @observable
    private endTime: Date = new Date(
        observeStore.time.getTime() + 30 * duration.second,
    );

    public async componentDidMount() {
        await this.fetchOnDemandStreams();
        if (this.form.meta.start && this.form.meta.end) {
            this.startTime = new Date(parseFloat(this.form.meta.start) * 1000);
            this.endTime = new Date(parseFloat(this.form.meta.end) * 1000);
        } else if (this.form.meta.start) {
            this.startTime = new Date(
                parseFloat(this.form.meta.start) * 1000 - 30 * duration.second,
            );
            this.endTime = new Date(
                parseFloat(this.form.meta.start) * 1000 + 30 * duration.second,
            );
        }
    }

    public render() {
        const { commandTemplate, device } = this.props;
        const { parameter, meta } = this.form;
        const {
            onSubmit,
            onChangeParameter,
            validation,
            loading,
            onLoading,
            time,
            onDemandStreams,
        } = this;

        const commandInfo = commandTemplate
            ? lookupCommandInfo(commandTemplate.command)
            : undefined;

        return (
            <Popup
                visible={this.visible}
                onClose={this.onClose}
                className={styles["new-command-popup"]}
            >
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    <Escape onEsc={this.onClose} />
                    <Heading3 className={styles.heading}>
                        {commandTemplate.name}
                    </Heading3>

                    {commandTemplate.command === "formant.demand_data" && (
                        <>
                            <p>
                                Retrieve data stored on {device.name} around{" "}
                                {formatDateTime(time)}.<br />
                            </p>
                            <DateTimeField
                                label="Start Time:"
                                time={this.startTime}
                                onTimeChanged={this.onChangeStartTime}
                            />
                            <DateTimeField
                                label="End Time:"
                                time={this.endTime}
                                onTimeChanged={this.onChangeEndTime}
                            />
                            <p>
                                Streams included:{" "}
                                {meta.streams ||
                                    "all on-demand streams" +
                                        (onDemandStreams
                                            ? ` (${onDemandStreams.length} streams)`
                                            : "")}
                            </p>
                        </>
                    )}

                    {commandTemplate.command !== "formant.demand_data" && (
                        <>
                            <p>
                                Issue command "{commandTemplate.name}" to device
                                "{device.name}"?
                            </p>
                            {(commandTemplate.description ||
                                commandInfo?.commandDescription) && (
                                <p className={styles.description}>
                                    {commandTemplate.description ||
                                        commandInfo?.commandDescription}
                                </p>
                            )}
                            {commandTemplate.parameterEnabled && (
                                <TextField
                                    label="Command parameters"
                                    placeholder={
                                        commandInfo?.parameterDescription
                                    }
                                    className={styles.field}
                                    value={parameter}
                                    onChange={onChangeParameter}
                                    invalid={!!validation?.parameter}
                                    small={true}
                                    autoFocus={true}
                                    readOnly={
                                        !commandTemplate.allowParameterOverride &&
                                        !!commandTemplate.parameterValue
                                    }
                                />
                            )}
                        </>
                    )}

                    <ValidationMessages
                        className={styles.validation}
                        validation={validation}
                    />

                    <Buttons className={styles.buttons}>
                        <StandardButton
                            id="close-button"
                            onClick={this.onClose}
                            type="button"
                            data-testid="cancel-command"
                        >
                            Cancel
                        </StandardButton>
                        <StandardButton
                            primary={true}
                            loading={loading}
                            data-testid="issue-command"
                        >
                            {commandTemplate.command === "formant.demand_data"
                                ? "Retrieve data"
                                : "Issue command"}
                        </StandardButton>
                    </Buttons>
                </Form>
            </Popup>
        );
    }

    private onChangeParameter = (event: ChangeEvent<InputElement>) => {
        this.form.parameter = event.target.value;
    };

    private onSubmit = async () => {
        const { commandTemplate, device } = this.props;
        const { form } = this;
        const {
            allowParameterOverride,
            command: commandText,
            parameterEnabled,
            parameterValue,
            parameterMeta,
            id: commandTemplateId,
        } = defined(commandTemplate);
        // Pick the user specified parameter value if allowParameterOverride is set or
        // if no default value is specified in the command template
        const parameter = parameterEnabled
            ? ((allowParameterOverride || isNullOrEmpty(parameterValue)) &&
                  form.parameter) ||
              parameterValue
            : undefined;
        const value = parameter?.length ? parameter : undefined;
        const user = userStore.authenticated();
        if (!user) {
            return;
        }
        const start = toUnixTime(this.startTime).toString();
        const end = toUnixTime(this.endTime).toString();
        const command: ICommand = {
            organizationId: defined(authStore.organizationId),
            deviceId: device.id,
            userId: defined(authStore.userId),
            commandTemplateId,
            command: commandText,
            parameter: {
                value,
                meta: {
                    ...parameterMeta,
                    ...form.meta,
                    ...{ start, end },
                },
                scrubberTime: observeStore.time.toISOString(),
            },
        };
        this.validation = undefined;
        try {
            let message = "Command issued";
            if (commandText === "formant.demand_data") {
                await this.fetchOnDemandStreams();
                if (this.onDemandStreams && this.onDemandStreams.length === 0) {
                    this.validation = {
                        other: [
                            "No streams configured for on-demand ingestion.",
                        ],
                    };
                    return;
                }
                if (this.endTime.getTime() <= this.startTime.getTime()) {
                    this.validation = {
                        other: ["End time must be greater than start time."],
                    };
                    return;
                }
                const online = onlineDevicesStore.isOnline(device.id);
                if (online) {
                    message = "Retrieving on-demand data";
                } else {
                    message =
                        "Device is offline, on-demand data will be retrieved when next available.";
                }
            }
            await commandStore.create(command);
            await this.onClose();
            appStore.setSnackbar(message);
            track(message, { Command: command });
        } catch (error) {
            if (error instanceof ConnectionError) {
                this.validation = {
                    other: ["Network request failed. Please try again."],
                };
                return;
            }
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };

    private onClose = async () => {
        this.visible = false;
        await animate();
        appStore.commandTemplate = undefined;
        deviceStore.issuingCommand = false;
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };

    private onChangeStartTime = (time: Date): void => {
        this.startTime = time;
    };

    private onChangeEndTime = (time: Date): void => {
        this.endTime = time;
    };

    private fetchOnDemandStreams = async () => {
        const device = await deviceStore.fetchGet(
            defined(this.props.device.id),
        );
        if (!device || !device.id) {
            return;
        }

        const newVersion = device.desiredConfigurationVersion;

        if (!newVersion) {
            return;
        }

        const configuration = await deviceClient.getConfiguration(
            await authStore.token(),
            device.id,
            newVersion,
        );

        this.onDemandStreams = (configuration.document.telemetry?.streams || [])
            .filter(_ => _.onDemand)
            .map(_ => {
                return _.name;
            });
    };
}
